import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import Discussion from 'flarum/common/models/Discussion';
import Badge from 'flarum/common/components/Badge';
import humanTime from 'flarum/common/helpers/humanTime';
import labelText from '../common/utils/labelText';
import formatPrice from '../common/utils/formatPrice';
import ClassifiedsHeroCarousel from './components/ClassifiedsHeroCarousel';
import ClassifiedsBreadcrumb from './components/ClassifiedsBreadcrumb';
import ClassifiedsActions from './components/ClassifiedsActions';
import ClassifiedsSellerCard from './components/ClassifiedsSellerCard';

/**
 * For classifieds discussions we replace the entire DiscussionHero with a 2-column,
 * OLX-inspired layout: photo gallery on the left, all listing meta on the right.
 * For everything else, the standard hero (with flarum-tags color, etc.) renders
 * unchanged via the original implementation.
 */
export default function addListingHeroBadge() {
  extend(Discussion.prototype, 'badges', function (badges) {
    if (!this.isClassifieds()) return;
    const status = this.listingStatus() || 'active';
    if (status !== 'active') {
      badges.add('classifiedsStatus', m(Badge, {
        type: `classifieds-${status}`,
        icon: status === 'sold' ? 'fas fa-check-circle' : 'fas fa-flag-checkered',
        label: app.translator.trans(`flarum-classifieds.lib.statuses.${status}`)
      }), 20);
    }
  });
  override(DiscussionHero.prototype, 'view', function (original) {
    const discussion = this.attrs?.discussion;
    if (!discussion?.isClassifieds?.()) return original();
    const images = discussion.listingImages?.() || [];
    const label = discussion.listingLabel();
    const status = discussion.listingStatus() || 'active';
    const price = formatPrice(discussion.listingPrice(), discussion.listingPriceMax(), discussion.listingCurrency());
    const location = discussion.listingLocation();
    const date = discussion.lastPostedAt?.() || discussion.createdAt?.();
    const tags = discussion.tags?.() || [];

    // Pick the first classifieds tag's color (or any tag's color as fallback)
    // to tint the hero subtly. Falls back to the theme primary if no color.
    const classifiedsTag = tags.find(t => t && typeof t.isClassifieds === 'function' && t.isClassifieds());
    const tintTag = classifiedsTag || tags.find(t => t && typeof t.color === 'function' && t.color());
    const tintColor = tintTag && tintTag.color?.() || '';
    const tagPills = tags.filter(t => t && typeof t.color === 'function').map(tag => m("a", {
      className: "TagLabel colored text-contrast--light",
      style: {
        '--tag-bg': tag.color()
      },
      href: app.route('tag', {
        tags: tag.slug()
      }),
      config: m.route.link,
      title: tag.description?.() || ''
    }, m("span", {
      className: "TagLabel-text"
    }, tag.icon?.() && m("i", {
      className: `TagLabel-icon icon text-colored ${tag.icon()}`
    }), m("span", {
      className: "TagLabel-name"
    }, tag.name?.()))));
    return m("header", {
      className: "Hero DiscussionHero DiscussionHero--classifieds",
      style: tintColor ? {
        '--classifieds-tint': tintColor
      } : undefined
    }, m("div", {
      className: "container"
    }, m(ClassifiedsBreadcrumb, {
      discussion: discussion
    }), m("div", {
      className: "ClassifiedsHero"
    }, m("div", {
      className: "ClassifiedsHero-gallery"
    }, images.length ? m(ClassifiedsHeroCarousel, {
      images: images,
      alt: discussion.title()
    }) : m("div", {
      className: "ClassifiedsHero-noImage"
    }, m("i", {
      className: "fas fa-camera",
      "aria-hidden": "true"
    }))), m("div", {
      className: "ClassifiedsHero-info"
    }, date && m("div", {
      className: "ClassifiedsHero-postedAt"
    }, m("i", {
      className: "far fa-clock",
      "aria-hidden": "true"
    }), " ", humanTime(date)), m("h1", {
      className: "ClassifiedsHero-title"
    }, discussion.title()), price && m("div", {
      className: "ClassifiedsHero-price"
    }, price), (label || status !== 'active' || tagPills.length) && m("div", {
      className: "ClassifiedsHero-chips"
    }, tagPills.length > 0 && m("span", {
      className: "TagsLabel"
    }, tagPills), label && m("span", {
      className: `TagLabel colored text-contrast--light ClassifiedsTagLabel ClassifiedsTagLabel--${String(label).toLowerCase()}`,
      title: labelText(label)
    }, m("span", {
      className: "TagLabel-text"
    }, m("i", {
      className: "TagLabel-icon icon text-colored fas fa-tag"
    }), m("span", {
      className: "TagLabel-name"
    }, labelText(label)))), status !== 'active' && m("span", {
      className: `ClassifiedsHero-statusBadge ClassifiedsHero-statusBadge--${status}`
    }, m("i", {
      className: 'fas ' + (status === 'sold' ? 'fa-check-circle' : 'fa-flag-checkered'),
      "aria-hidden": "true"
    }), ' ', app.translator.trans(`flarum-classifieds.lib.statuses.${status}`))), location && m("div", {
      className: "ClassifiedsHero-location"
    }, m("i", {
      className: "fas fa-map-marker-alt",
      "aria-hidden": "true"
    }), m("span", null, location)), m(ClassifiedsActions, {
      discussion: discussion
    }), m("div", {
      className: "ClassifiedsHero-divider",
      "aria-hidden": "true"
    }), m(ClassifiedsSellerCard, {
      discussion: discussion
    })))));
  });
}